/**
 * Inter-page link resolution shared between weave (export-time wikilink expansion)
 * and the client LinkNavigationEnricher (preview-time click interception).
 *
 * Pages on disk use the `.src.md` extension. Exported markdown uses `.md`.
 * Authors write `.md` (the exported form); this resolver maps either form to
 * an existing page in the project file list.
 */

export interface ResolvedLink {
  /** The path of the page in the project (always with `.src.md` extension). */
  path: string;
  /** Optional URL fragment (#section) from the link. */
  anchor: string | null;
}

/** Strip the section anchor off a link target and return both parts. */
export function splitAnchor(target: string): { base: string; anchor: string | null } {
  const hashIdx = target.indexOf('#');
  if (hashIdx === -1) return { base: target, anchor: null };
  return {
    base: target.slice(0, hashIdx),
    anchor: target.slice(hashIdx + 1) || null,
  };
}

/**
 * Normalize a target to its `.src.md` page form for lookup.
 * - `foo.md` -> `foo.src.md`
 * - `foo.src.md` -> `foo.src.md`
 * - `foo` (no extension) -> `foo.src.md`
 */
export function toPagePath(target: string): string {
  if (target.endsWith('.src.md')) return target;
  if (target.endsWith('.md')) return target.slice(0, -3) + '.src.md';
  return target + '.src.md';
}

/**
 * Resolve a relative/absolute path against the directory of `fromFile`.
 * Returns a normalized POSIX path with no leading slash.
 */
export function resolveAgainst(fromFile: string | null, target: string): string {
  // Strip leading `./`
  let t = target.replace(/^\.\//, '');

  // Absolute (project-root) path: leading `/`
  if (t.startsWith('/')) {
    return normalizePath(t.slice(1));
  }

  // No source file context — treat as project-root relative.
  if (!fromFile) {
    return normalizePath(t);
  }

  const fromDir = fromFile.includes('/')
    ? fromFile.slice(0, fromFile.lastIndexOf('/'))
    : '';

  // Same-folder bare name
  if (!t.startsWith('../') && !t.includes('/')) {
    return normalizePath(fromDir ? `${fromDir}/${t}` : t);
  }

  // Relative — resolve `..` segments
  const baseParts = fromDir ? fromDir.split('/') : [];
  const targetParts = t.split('/');
  for (const part of targetParts) {
    if (part === '..') {
      baseParts.pop();
    } else if (part !== '.' && part !== '') {
      baseParts.push(part);
    }
  }
  return baseParts.join('/');
}

/** Collapse duplicate slashes and resolve `.`/`..` segments in a posix-style path. */
function normalizePath(p: string): string {
  const parts = p.split('/').filter(Boolean);
  const stack: string[] = [];
  for (const part of parts) {
    if (part === '..') {
      stack.pop();
    } else if (part !== '.') {
      stack.push(part);
    }
  }
  return stack.join('/');
}

/**
 * Resolve a markdown link target to an existing page path in the project.
 *
 * Resolution order:
 *   1. Treat target as path relative to `fromFile`, normalize, look up in `allPaths`
 *   2. Treat target as project-root absolute path, look up in `allPaths`
 *   3. Treat the bare basename and find a unique page with that name
 *
 * Returns `null` if no unique page matches.
 *
 * @param target   Link target as authored (e.g. `foo.md`, `../setup/install.md`, `setup/install`)
 * @param fromFile The page that contains the link, e.g. `main/page.src.md` (null for root context)
 * @param allPaths All page paths in the project (with `.src.md` extension)
 */
export function resolveLink(
  target: string,
  fromFile: string | null,
  allPaths: readonly string[]
): ResolvedLink | null {
  if (!target) return null;

  const { base, anchor } = splitAnchor(target);
  if (!base) {
    // Pure anchor link — same page
    return fromFile ? { path: fromFile, anchor } : null;
  }

  const pathSet = new Set(allPaths);

  // 1. Resolve relative to source file.
  const relPath = toPagePath(resolveAgainst(fromFile, base));
  if (pathSet.has(relPath)) {
    return { path: relPath, anchor };
  }

  // 2. Try project-root absolute (in case the target has slashes but author didn't add leading /).
  if (!base.startsWith('/') && !base.startsWith('./') && !base.startsWith('../')) {
    const rootPath = toPagePath(base);
    if (pathSet.has(rootPath)) {
      return { path: rootPath, anchor };
    }
  }

  // 3. Bare-name fuzzy match.
  const baseName = base.split('/').pop()!;
  const wantedFile = toPagePath(baseName);
  const matches = allPaths.filter(p => p === wantedFile || p.endsWith('/' + wantedFile));
  if (matches.length === 1) {
    return { path: matches[0], anchor };
  }

  return null;
}

/**
 * Compute a relative path from one file to another within the project.
 * Used to rewrite wikilinks to portable relative markdown links.
 *
 * @example
 *   relativeBetween('main/page.src.md', 'setup/install.src.md') === '../setup/install.src.md'
 *   relativeBetween('main/page.src.md', 'main/other.src.md') === 'other.src.md'
 */
export function relativeBetween(fromFile: string, toFile: string): string {
  const fromParts = fromFile.split('/');
  fromParts.pop(); // drop filename
  const toParts = toFile.split('/');

  // Find common prefix
  let common = 0;
  while (
    common < fromParts.length &&
    common < toParts.length - 1 &&
    fromParts[common] === toParts[common]
  ) {
    common++;
  }

  const upSegments = fromParts.length - common;
  const downSegments = toParts.slice(common);
  const segments = [...Array(upSegments).fill('..'), ...downSegments];
  return segments.length ? segments.join('/') : toParts[toParts.length - 1];
}
